import type { ProjectDownload } from './common';
import type { DownloadTarget } from './fileDownload';
import { type DownloadStatus, getDownloadStatus } from './status';

/**
 * The history is stored one row per requested file format. Users submit one export, so the history
 * shows one row per submission — this folds the flat list back into those runs.
 *
 * Kept free of runtime imports so it can be tested without loading the page's component tree.
 */

/** One requested format within a run, carrying the status of its own extract. */
export interface ExportRunFormat {
  downloadId: string;
  format: string | null;
  status: DownloadStatus;
  hasFile?: boolean;
}

export type ExportRunStatus = 'pending' | 'download' | 'no-data' | 'failed' | 'delayed' | null;

export interface ExportRun {
  /** Stable key for the run: the submission it belongs to, or its own row when it predates submissions. */
  id: string;
  submissionId: string | null;
  createdAt: string;
  user: ProjectDownload['user'];
  projectId: string;
  organizationId: string;
  layers: string[];
  projection?: ProjectDownload['projection'];
  // Null is CivilGrid Default. Every row of a submission stores the same id.
  extractTemplateId: string | null;
  formats: ExportRunFormat[];
  status: ExportRunStatus;
  /** Which object the run's download button signs, or null when there is nothing to hand over yet. */
  download: Omit<DownloadTarget, 'projectId'> | null;
}

/** The row and object the run's download button signs, or null when the run has nothing to hand over. */
function resolveDownload(
  rows: ProjectDownload[],
  formats: ExportRunFormat[],
  submissionId: string | null,
): Omit<DownloadTarget, 'projectId'> | null {
  // Keyed by its own id, so this group is the one pre-submission row and it signs its own file.
  if (submissionId === null) {
    const [legacy] = rows;
    return legacy?.hasFile ? { downloadId: legacy.id, target: 'file' } : null;
  }
  // combinedResultPath is stored whenever FME resolved, including an archive that held nothing usable.
  if (!formats.some((format) => format.status === 'download')) return null;

  // The shared archive is copied onto every row, so any row that has it names the object to sign.
  const combined = rows.find((row) => row.hasCombinedArchive);
  if (combined) return { downloadId: combined.id, target: 'combined' };

  // A submission from before combined archives has only each format's own file.
  const delivered = formats.find((format) => format.hasFile);
  return delivered ? { downloadId: delivered.downloadId, target: 'file' } : null;
}

/**
 * The format rows record what the run actually did, so they decide its state; the archive only says
 * whether there is a file to hand over. A run in flight or with a failed format is neither, however
 * its archive resolved.
 */
function aggregateStatus(
  formats: ExportRunFormat[],
  download: Omit<DownloadTarget, 'projectId'> | null,
): ExportRunStatus {
  if (formats.some((format) => format.status === 'pending')) return 'pending';
  // Uniform across a submission's rows (see getDownloadStatus) — a restricted run is waiting on an
  // admin, not on FME, so this takes priority over a failed/no-data format.
  if (formats.some((format) => format.status === 'delayed')) return 'delayed';
  if (formats.some((format) => format.status === 'failed')) return 'failed';
  if (download) return 'download';
  if (formats.some((format) => format.status === 'no-data')) return 'no-data';

  return null;
}

/**
 * Groups downloads into runs, preserving the order the server returned them in (newest first, and
 * format-ascending within a submission).
 */
export function toExportRuns(downloads: ProjectDownload[]): ExportRun[] {
  const runs = new Map<string, ProjectDownload[]>();

  for (const download of downloads) {
    const key = download.submissionId ?? download.id;
    const existing = runs.get(key);

    if (existing) {
      existing.push(download);
    } else {
      runs.set(key, [download]);
    }
  }

  return Array.from(runs, ([id, rows]) => {
    const [first] = rows;
    const submissionId = first.submissionId ?? null;
    const formats: ExportRunFormat[] = rows.map((row) => ({
      downloadId: row.id,
      format: row.downloadType ?? null,
      status: getDownloadStatus(row),
      hasFile: row.hasFile,
    }));
    const download = resolveDownload(rows, formats, submissionId);

    return {
      id,
      submissionId,
      createdAt: first.createdAt,
      user: first.user,
      projectId: first.projectId,
      organizationId: first.organizationId,
      // Every row in a submission requests the same layers, projection, and template, so the first speaks for the run.
      layers: first.layers ?? [],
      projection: first.projection,
      extractTemplateId: first.extractTemplateId ?? null,
      formats,
      status: aggregateStatus(formats, download),
      download,
    };
  });
}

/** Normal cadence for a run FME is still working on. */
export const POLL_INTERVAL_MS = 10000;

/**
 * The API accepts a submission before the extract job writes its rows, so a just-submitted run is
 * missing for a beat. Poll hard over that beat rather than leaving the history empty for a whole
 * normal interval.
 */
export const AWAITING_POLL_INTERVAL_MS = 1000;

/** How long to keep waiting on rows that never arrive before dropping back to the normal cadence. */
export const AWAITING_POLL_TIMEOUT_MS = 15000;

/**
 * How often to refetch the history, or `null` when nothing is outstanding — this panel stays open
 * while the user picks layers, so it must not poll for its own sake.
 */
export function getHistoryPollInterval(params: {
  awaitingSubmissionId: string | null;
  runs: Pick<ExportRun, 'status'>[];
}): number | null {
  if (params.awaitingSubmissionId !== null) return AWAITING_POLL_INTERVAL_MS;
  if (params.runs.some((run) => run.status === 'pending')) return POLL_INTERVAL_MS;

  return null;
}
